import React, { useState, useEffect, useRef } from 'react';
import { Popover, Input, Button, Space, message, type InputRef } from 'antd';
import { useTheme } from '../../contexts/useTheme';

interface EditableParameterPopoverProps {
  displayName: string;
  currentValue: string;
  unit?: string | null;
  onSave: (newValue: string) => Promise<void>;
  children: React.ReactNode;
}

export const EditableParameterPopover: React.FC<EditableParameterPopoverProps> = ({
  displayName,
  currentValue,
  unit,
  onSave,
  children,
}) => {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState(currentValue);
  const [saving, setSaving] = useState(false);
  const inputRef = useRef<InputRef>(null);

  useEffect(() => {
    if (open) {
      setValue(currentValue);
      setTimeout(() => inputRef.current?.focus?.({ cursor: 'all' }), 100);
    }
  }, [open, currentValue]);

  const handleSave = async () => {
    if (value === currentValue) {
      setOpen(false);
      return;
    }
    setSaving(true);
    try {
      await onSave(value);
      message.success(`${displayName} updated`);
      setOpen(false);
    } catch (error) {
      const msg = error instanceof Error ? error.message : 'Failed to save';
      message.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSave();
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  const content = (
    <div style={{ width: 200 }}>
      <div style={{ marginBottom: 8, fontSize: 12, color: isDark ? '#999' : '#666' }}>
        {displayName}{unit ? ` (${unit})` : ''}
      </div>
      <Input
        ref={inputRef}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={handleKeyDown}
        size="small"
        disabled={saving}
        style={{ marginBottom: 8 }}
      />
      <Space size="small" style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button size="small" onClick={() => setOpen(false)} disabled={saving}>
          Cancel
        </Button>
        <Button
          size="small"
          type="primary"
          onClick={handleSave}
          loading={saving}
          disabled={value === currentValue}
        >
          Save
        </Button>
      </Space>
    </div>
  );

  return (
    <Popover
      content={content}
      trigger="click"
      open={open}
      onOpenChange={(visible) => {
        if (!saving) setOpen(visible);
      }}
      placement="top"
    >
      <span
        style={{
          cursor: 'pointer',
          borderBottom: `1px dashed ${isDark ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.2)'}`,
        }}
        onClick={(e) => {
          e.stopPropagation();
          setOpen(true);
        }}
      >
        {children}
      </span>
    </Popover>
  );
};
